/**
 * External Accountant WorkDesk — Phase 3.1
 *
 * Consolidates AccountantHome (5 priority buckets) and AccountantSharedReports
 * (legacy shared-reports list) into a single unified feed per Atlas §2.
 *
 * Design: Black+Orange (near-black surfaces, #FF6600 brand signal only,
 * #22C55E done-green for signed-off states).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from '@/lib/http';
import {
  Inbox,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Archive,
  ChevronRight,
  Search,
  FileText,
  Building2,
  Calendar,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { useAccountantAuth } from '../../../contexts/AccountantAuthContext';
import { isTestMode } from '../../../contexts/TestAuthContext';
import { BRAND_NAME } from '../../../config/brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface SharedReport {
  report_id: string;
  client_name: string;
  claimant_name: string;
  business_address: string;
  shared_by_name: string;
  shared_at: string;
  last_accessed?: string;
  permissions: string[];
  status: string;
  event_type?: string;
  coverage_year?: number;
  // Canonical sworn net (policy_deductions.net_claim) from /reports/shared;
  // final_claim_amount is the stale snapshot fallback only.
  net_supported_claim?: number | null;
  final_claim_amount?: number;
}

type CategoryId =
  | 'pending_first_review'
  | 'stale_after_rerun'
  | 'open_disputes'
  | 'ready_for_signoff'
  | 'recently_attested';

interface CategoryDef {
  id: CategoryId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accentClass: string;
  emptyCopy: string;
}

const CATEGORIES: CategoryDef[] = [
  {
    id: 'pending_first_review',
    label: 'Pending first review',
    description: 'Engagements assigned to you but not yet opened.',
    icon: Inbox,
    accentClass: 'text-brand',
    emptyCopy: 'No new engagements awaiting your initial review.',
  },
  {
    id: 'stale_after_rerun',
    label: 'Stale after rerun',
    description: 'An upstream engine changed — your prior review is invalidated.',
    icon: AlertTriangle,
    accentClass: 'text-brand',
    emptyCopy: 'No engagements have been invalidated by recent reruns.',
  },
  {
    id: 'open_disputes',
    label: 'Open disputes',
    description: 'Engines you flagged that are awaiting in-house resolution.',
    icon: AlertCircle,
    accentClass: 'text-brand',
    emptyCopy: 'No disputes currently in flight.',
  },
  {
    id: 'ready_for_signoff',
    label: 'Ready for sign-off',
    description: 'All required engines approved — final attestation pending your signature.',
    icon: CheckCircle2,
    accentClass: 'text-done',
    emptyCopy: 'No engagements ready for final sign-off.',
  },
  {
    id: 'recently_attested',
    label: 'Recently attested',
    description: 'Engagements you signed off in the last 14 days.',
    icon: Archive,
    accentClass: 'text-text-muted',
    emptyCopy: 'No recently completed engagements.',
  },
];

function categorize(reports: SharedReport[]): Record<CategoryId, SharedReport[]> {
  const out: Record<CategoryId, SharedReport[]> = {
    pending_first_review: [],
    stale_after_rerun: [],
    open_disputes: [],
    ready_for_signoff: [],
    recently_attested: [],
  };

  for (const report of reports) {
    const status = (report.status || '').toLowerCase();
    if (status === 'finalized' || status === 'attested' || status === 'signed_off') {
      out.recently_attested.push(report);
    } else if (status === 'ready_for_signoff' || status === 'awaiting_signoff') {
      out.ready_for_signoff.push(report);
    } else if (status === 'disputed' || status === 'in_dispute') {
      out.open_disputes.push(report);
    } else if (status === 'stale' || status === 'stale_after_rerun') {
      out.stale_after_rerun.push(report);
    } else if (!report.last_accessed) {
      out.pending_first_review.push(report);
    } else {
      out.pending_first_review.push(report);
    }
  }
  return out;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ReportRow({ report }: { report: SharedReport }) {
  return (
    <Link
      to={`/accountant/reports/${report.report_id}`}
      className="flex items-center justify-between px-4 py-3 hover:bg-bg-hover group transition-colors"
      data-testid={`home-report-row-${report.report_id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary truncate group-hover:text-brand transition-colors">
          {report.client_name || report.claimant_name || 'Untitled engagement'}
        </p>
        <p className="text-xs text-text-muted truncate">
          Shared by {report.shared_by_name} · {formatDate(report.shared_at)}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-text-dim group-hover:text-text-secondary flex-shrink-0 ml-3" />
    </Link>
  );
}

function CategorySection({
  def,
  reports,
}: {
  def: CategoryDef;
  reports: SharedReport[];
}) {
  const Icon = def.icon;
  return (
    <section
      className="bg-bg-surface border border-border rounded-xl overflow-hidden"
      data-testid={`home-category-${def.id}`}
    >
      <header className="px-4 py-3 border-b border-border-sub flex items-center gap-3">
        <Icon className={`w-5 h-5 ${def.accentClass}`} />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-text-primary">{def.label}</h2>
          <p className="text-xs text-text-muted mt-0.5">{def.description}</p>
        </div>
        <span className="text-xs font-medium text-text-muted bg-bg-elevated px-2 py-0.5 rounded-full flex-shrink-0">
          {reports.length}
        </span>
      </header>
      {reports.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-xs text-text-dim italic">{def.emptyCopy}</p>
        </div>
      ) : (
        <div className="divide-y divide-border-faint">
          {reports.map((r) => (
            <ReportRow key={r.report_id} report={r} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function ExternalAccountantWorkDesk() {
  const { token, accountant, loading: authLoading } = useAccountantAuth();
  const [reports, setReports] = useState<SharedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [serverBuckets, setServerBuckets] = useState<Record<CategoryId, SharedReport[]> | null>(null);
  const [serverTotalActive, setServerTotalActive] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'buckets' | 'list'>('buckets');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');

        try {
          const feedRes = await axios.get(
            `${BACKEND_URL}/api/v1/external-accountant/home-feed`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (cancelled) return;
          const data = feedRes.data;
          if (data && typeof data === 'object' && data.buckets) {
            const flat: Record<CategoryId, SharedReport[]> = {
              pending_first_review: data.buckets.pending_first_review?.items || [],
              stale_after_rerun: data.buckets.stale_after_rerun?.items || [],
              open_disputes: data.buckets.open_disputes?.items || [],
              ready_for_signoff: data.buckets.ready_for_signoff?.items || [],
              recently_attested: data.buckets.recently_attested?.items || [],
            };
            setServerBuckets(flat);
            setServerTotalActive(data.total_active ?? null);
            const allRows: SharedReport[] = [
              ...flat.pending_first_review,
              ...flat.stale_after_rerun,
              ...flat.open_disputes,
              ...flat.ready_for_signoff,
              ...flat.recently_attested,
            ];
            setReports(allRows);
            return;
          }
        } catch (feedErr: any) {
          if ([401, 403].includes(feedErr?.response?.status)) {
            throw feedErr;
          }
        }

        const res = await axios.get(
          `${BACKEND_URL}/api/v1/collaboration/reports/shared`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.reports || [];
        setReports(list);
        setServerBuckets(null);
        setServerTotalActive(null);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Failed to load engagements');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const buckets = useMemo(() => serverBuckets ?? categorize(reports), [serverBuckets, reports]);

  const totalActive =
    serverTotalActive ??
    buckets.pending_first_review.length +
      buckets.stale_after_rerun.length +
      buckets.open_disputes.length +
      buckets.ready_for_signoff.length;

  const filteredReports = useMemo(() => {
    if (!searchTerm.trim()) return reports;
    const term = searchTerm.toLowerCase();
    return reports.filter(
      (r) =>
        r.client_name?.toLowerCase().includes(term) ||
        r.claimant_name?.toLowerCase().includes(term) ||
        r.business_address?.toLowerCase().includes(term),
    );
  }, [reports, searchTerm]);

  const getStatusBadgeClass = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'attested' || s === 'signed_off' || s === 'ready_for_signoff')
      return 'bg-done-bg text-done border-done/20';
    if (s === 'pending') return 'bg-brand/10 text-brand border-brand/20';
    if (s === 'review' || s === 'in_review') return 'bg-text-muted/10 text-text-muted border-text-muted/20';
    return 'bg-bg-elevated text-text-muted border-border';
  };

  // JWT auth guard (this route lives in the external-accountant JWT tree, NOT behind
  // Clerk). Gate on the context `loading` flag FIRST — the provider hydrates the token
  // from localStorage asynchronously, so a bare `!token` check would bounce a valid
  // stored session on hard refresh. Placed after all hooks (rules-of-hooks).
  if (authLoading) return null;
  if (!token && !isTestMode) return <Navigate to="/accountant/login" replace />;

  return (
    <div className="min-h-screen bg-bg-base" data-testid="external-accountant-workdesk">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-text-primary" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            {accountant?.full_name ? `Welcome back, ${accountant.full_name.split(' ')[0]}` : 'Welcome back'}
          </h1>
          <p className="text-sm text-text-muted mt-1">
            {loading
              ? 'Loading your engagements…'
              : totalActive === 0
              ? 'No engagements need your attention right now.'
              : `${totalActive} engagement${totalActive !== 1 ? 's' : ''} need${totalActive === 1 ? 's' : ''} your attention.`}
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-brand/5 border border-brand/20 rounded-lg p-4 text-sm text-brand">
            {error}
          </div>
        )}

        {/* View Toggle + Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex items-center gap-2 bg-bg-surface border border-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('buckets')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'buckets'
                  ? 'bg-brand'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              style={viewMode === 'buckets' ? { color: 'var(--color-on-brand)' } : undefined}
            >
              Priority Buckets
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-brand'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              style={viewMode === 'list' ? { color: 'var(--color-on-brand)' } : undefined}
            >
              All Reports
            </button>
          </div>

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" />
            <input
              type="text"
              placeholder="Search by client, business, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-surface border border-border rounded-lg text-sm text-text-primary placeholder:text-text-dim focus:outline-none focus:ring-2 focus:ring-brand/50"
            />
          </div>

          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-bg-surface border border-border rounded-lg text-sm font-medium text-text-secondary hover:bg-bg-hover transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Bucket View */}
        {viewMode === 'buckets' && (
          <div className="space-y-4">
            {CATEGORIES.map((def) => (
              <CategorySection key={def.id} def={def} reports={buckets[def.id]} />
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 text-text-dim animate-spin mx-auto mb-4" />
                <p className="text-text-muted">Loading reports...</p>
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-text-dim mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">No Reports Found</h3>
                <p className="text-text-muted">
                  {searchTerm ? 'Try adjusting your search' : 'Reports shared with you will appear here'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-bg-elevated border-b border-border">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Client / Business
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Event
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Claim Amount
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Shared
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-faint">
                    {filteredReports.map((report) => (
                      <tr
                        key={report.report_id}
                        className="hover:bg-bg-hover cursor-pointer transition-colors"
                        onClick={() => window.location.href = `/accountant/reports/${report.report_id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-bg-elevated rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-brand" />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary">
                                {report.claimant_name || report.client_name}
                              </p>
                              <p className="text-sm text-text-muted truncate max-w-[200px]">
                                {report.business_address}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-text-primary">{report.event_type || '-'}</p>
                          <p className="text-xs text-text-muted">{report.coverage_year || '-'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-semibold text-text-primary" style={{ fontFamily: "'DM Mono', monospace" }}>
                            <FinancialValue value={report.net_supported_claim ?? report.final_claim_amount} />
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-sm text-text-muted">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(report.shared_at)}
                          </div>
                          <p className="text-xs text-text-dim mt-0.5">by {report.shared_by_name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeClass(
                              report.status
                            )}`}
                          >
                            {report.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex items-center gap-1 text-brand hover:text-brand-hover text-sm font-medium">
                            <Eye className="w-4 h-4" />
                            View
                            <ChevronRight className="w-4 h-4" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Legacy fallback note */}
        {serverBuckets === null && reports.length > 0 && viewMode === 'buckets' && (
          <p className="mt-6 text-xs text-text-dim italic">
            Showing best-effort categorization from shared-report status. The dedicated home-feed endpoint
            is unavailable; engine-level signals (stale, disputes) may be incomplete.
          </p>
        )}

        {/* Summary footer for list view */}
        {!loading && viewMode === 'list' && filteredReports.length > 0 && (
          <div className="mt-4 text-sm text-text-muted text-center">
            Showing {filteredReports.length} of {reports.length} reports
          </div>
        )}
      </div>
    </div>
  );
}
